import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Dialog,
  Box,
  InputBase,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as InvoiceIcon,
  Business as ClientIcon,
  Person as PersonIcon,
  ArrowForward as ScreenIcon,
} from '@mui/icons-material';
import { globalSearch } from '../api';
import { formatCurrency } from '../utils/formatUtils';
import StatusChip from './StatusChip';
import { useTerms } from '../context/TerminologyContext';

// ⌘K command palette: jump to a screen, invoice, client, or person.
// Screens come from the caller's rendered tab list (permission-filtered);
// data results come from /api/search (admin-mode roles only).

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = String(d).split('T')[0].split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, day)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

export default function CommandPalette({ open, onClose, tabs, onNavigate, canSearchData }) {
  const { t, navLabel } = useTerms();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ invoices: [], companies: [], employees: [] });
  const [highlight, setHighlight] = useState(0);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults({ invoices: [], companies: [], employees: [] });
      setHighlight(0);
    }
  }, [open]);

  // Debounced server search
  useEffect(() => {
    if (!open || !canSearchData) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setResults({ invoices: [], companies: [], employees: [] });
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      const seq = ++requestSeq.current;
      try {
        const res = await globalSearch(q);
        // Ignore responses that arrive after a newer query
        if (seq === requestSeq.current) setResults(res.data);
      } catch {
        if (seq === requestSeq.current) setResults({ invoices: [], companies: [], employees: [] });
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, canSearchData]);

  // Flat, ordered result list for keyboard navigation
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = [];

    const screens = q.length >= 1
      ? tabs.filter(tab => navLabel(tab.label).toLowerCase().includes(q) || tab.label.toLowerCase().includes(q)).slice(0, 5)
      : [];
    screens.forEach(tab => out.push({
      group: 'Screens', key: `screen-${tab.label}`,
      icon: <ScreenIcon fontSize="small" />, primary: navLabel(tab.label),
      action: () => onNavigate(tab.label),
    }));

    results.invoices.forEach(inv => out.push({
      group: 'Invoices', key: `inv-${inv.id}`,
      icon: <InvoiceIcon fontSize="small" />,
      primary: inv.invoice_number,
      secondary: `${inv.company_name || ''} · ${fmtDate(inv.invoice_date)} · ${formatCurrency(inv.total_amount)}`,
      chip: inv.status,
      action: () => onNavigate('Invoices'),
    }));
    results.companies.forEach(c => out.push({
      group: t('client.plural'), key: `co-${c.id}`,
      icon: <ClientIcon fontSize="small" />, primary: c.name,
      secondary: c.payment_terms || null,
      action: () => onNavigate('Clients'),
    }));
    results.employees.forEach(e => out.push({
      group: 'People', key: `emp-${e.id}`,
      icon: <PersonIcon fontSize="small" />,
      primary: `${e.first_name} ${e.last_name}`,
      secondary: e.email,
      chip: e.is_active ? null : 'inactive',
      action: () => onNavigate('People'),
    }));
    return out;
  }, [query, results, tabs, onNavigate, t, navLabel]);

  useEffect(() => {
    setHighlight(h => Math.min(h, Math.max(0, items.length - 1)));
  }, [items.length]);

  const select = useCallback((item) => {
    if (!item) return;
    item.action();
    onClose();
  }, [onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(items[highlight]);
    }
  };

  // Render with group headers
  let lastGroup = null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { mt: -20, borderRadius: '10px' } }}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start', pt: '18vh' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <SearchIcon sx={{ fontSize: 18, color: 'text.faint' }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder={canSearchData ? 'Search invoices, people, clients, screens…' : 'Search screens…'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ fontSize: '0.875rem' }}
          inputProps={{ 'aria-label': 'Global search' }}
        />
        <Box component="kbd" sx={{ fontSize: '0.6875rem', border: 1, borderColor: 'divider', borderRadius: '4px', px: 0.75, py: 0.25, color: 'text.faint', bgcolor: 'background.surface2', fontFamily: 'inherit' }}>
          esc
        </Box>
      </Box>

      {items.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.faint', textAlign: 'center', py: 4 }}>
          {query.trim().length < 2 ? 'Type to search…' : 'No matches'}
        </Typography>
      ) : (
        <List dense disablePadding sx={{ maxHeight: 380, overflowY: 'auto', py: 0.5 }}>
          {items.map((item, i) => {
            const header = item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;
            return (
              <React.Fragment key={item.key}>
                {header && (
                  <>
                    {i > 0 && <Divider sx={{ my: 0.5 }} />}
                    <Typography sx={{ px: 2, pt: 1, pb: 0.25, fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.faint' }}>
                      {header}
                    </Typography>
                  </>
                )}
                <ListItemButton
                  selected={i === highlight}
                  onClick={() => select(item)}
                  onMouseEnter={() => setHighlight(i)}
                  sx={{ mx: 1, borderRadius: '6px', '&.Mui-selected': { bgcolor: 'primary.soft' } }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.primary}
                    secondary={item.secondary}
                    primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: '0.6875rem', noWrap: true }}
                  />
                  {item.chip && <StatusChip status={item.chip} sx={{ ml: 1 }} />}
                </ListItemButton>
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Dialog>
  );
}
